import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import { useSelectedModel } from "@/hooks/useSelectedModel";
import { useModels } from "@/hooks/useModels";
import { useModelWorkspace } from "@/contexts/ModelWorkspaceContext";
import {
  DEFAULT_TRAINING_PARAMS,
  trainClassification,
  trainClustering,
  trainRegression,
  type TrainingParams,
} from "@/lib/ml";

export const ModelTuning = () => {
  const { model } = useSelectedModel();
  const { dataset, markTrained } = useModelWorkspace();
  const { saveTrainedModel } = useModels();
  const [params, setParams] = useState<TrainingParams>(DEFAULT_TRAINING_PARAMS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const p = (model?.parameters ?? {}) as Partial<TrainingParams>;
    setParams({
      learningRate: p.learningRate ?? DEFAULT_TRAINING_PARAMS.learningRate,
      epochs: p.epochs ?? DEFAULT_TRAINING_PARAMS.epochs,
      testSplit: p.testSplit ?? DEFAULT_TRAINING_PARAMS.testSplit,
    });
  }, [model?.id]);

  const rows = dataset?.rows ?? ((model?.training_data ?? []) as Record<string, any>[]);

  const retrain = async () => {
    if (!model) {
      toast.error("Train a model first.");
      return;
    }
    if (!rows.length) {
      toast.error("Load the data again in the builder above so the model can be retrained.");
      return;
    }
    const features = ((model.parameters as any)?.features ?? []) as string[];
    if (!features.length) {
      toast.error("This model has no saved input columns. Rebuild it above.");
      return;
    }

    setBusy(true);
    try {
      const trained =
        model.model_type === "classification"
          ? trainClassification(rows, model.target_variable!, features, params)
          : model.model_type === "clustering"
            ? trainClustering(rows, features, 3)
            : trainRegression(rows, model.target_variable!, features, params);

      const saved = await saveTrainedModel.mutateAsync({
        id: model.id,
        name: model.name,
        industry: model.industry ?? undefined,
        dataSource: model.data_source ?? "uploaded data",
        parameters: { ...params, model_type: model.model_type },
        trained,
        sampleRows: rows,
      });
      markTrained(saved.id);
      toast.success("Retrained with your settings — scores updated below.");
    } catch (e: any) {
      toast.error(e?.message || "Retraining failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-2">
        <Settings className="h-5 w-5" />
        <h2 className="text-xl font-semibold">Fine-tune</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {model ? `Adjusting "${model.name}"` : "Train a model first, then adjust it here."}
      </p>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">
            Learning rate (how big each step is)
          </label>
          <Input
            type="number"
            step="0.001"
            min="0.001"
            max="1"
            value={params.learningRate}
            onChange={(e) =>
              setParams((p) => ({ ...p, learningRate: Number(e.target.value) || p.learningRate }))
            }
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Passes over the data</label>
          <Input
            type="number"
            min="10"
            max="5000"
            value={params.epochs}
            onChange={(e) => setParams((p) => ({ ...p, epochs: Number(e.target.value) || p.epochs }))}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">
            Share of rows held back for testing
          </label>
          <Input
            type="number"
            step="0.05"
            min="0.1"
            max="0.5"
            value={params.testSplit}
            onChange={(e) =>
              setParams((p) => ({ ...p, testSplit: Number(e.target.value) || p.testSplit }))
            }
          />
        </div>

        <Button className="w-full" onClick={retrain} disabled={busy || !model}>
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Retraining...
            </>
          ) : (
            "Retrain with these settings"
          )}
        </Button>
      </div>
    </Card>
  );
};
